import React, { useState, useMemo } from "react";
import { useUserAndGameContext } from "../../../../contexts/UserAndGameContext";
import { useTiersGameContext } from "../../../TiersGames/context/TiersGameContext";
import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import TiersSingleEntryView from "./TiersSingleEntryView";
import TiersMultipleEntryView from "./TiersMultipleEntryView";

const TiersGameUserDash = ({ canIAddAnEntry, setIndexToRemove, setRemoveModalOpen }) => {
    const {
        allUsersEntries,
        usersIndexesInThisPool,
        myLeaderboardInfo,
        masterPoolData,
        pickIndex,
        intervalInfo,
        league,
        isThisPoolPartyGame,
        setJoinOpen,
        isUserAdmin,
        hasJoinDeadlinePassed,
        originalPicks,
        firstGameTime,
    } = useUserAndGameContext();

    const { 
        teams,
        theCurrentInterval,
        theCurrentIntervalsInfo,
        theCurrentIntervalsTiersData,
        oneIntervalEvent,
        firstInterval,
    } = useTiersGameContext();

    const nowInNewYork = DateTime.now().setZone('America/New_York');
    const currentIntervalsDeadline = theCurrentIntervalsInfo?.deadline;
    const theCurrentIntervalsDeadlineInNewYork = currentIntervalsDeadline ? DateTime.fromISO(currentIntervalsDeadline, { zone: 'America/New_York' }) : null;
    const hasCurrentIntervalStarted = theCurrentIntervalsDeadlineInNewYork ? theCurrentIntervalsDeadlineInNewYork < nowInNewYork : false;

    const isUserOnOverallLeaderboard = firstInterval === theCurrentInterval 
    ? true 
    : myLeaderboardInfo?.myInfo?.some(userEntry => 
        userEntry.intervalData?.some(intervalEntry => 
            intervalEntry.interval === firstInterval
        )
      ) || false;
    //console.log("Is User on Overall Leaderboard", isUserOnOverallLeaderboard, "First Interval", firstInterval, "Current Interval", theCurrentInterval, "My Leaderboard Info", myLeaderboardInfo);

    // Get entries for this pool
    const entriesInThisPool = usersIndexesInThisPool
    .filter(index => index >= 0 && index < allUsersEntries.length)
    .map(index => allUsersEntries[index]);

    // function the rank 
    function findMyRank(myLeaderboardInfo, entryName, interval = null) {
        //console.log("findMyRank", myLeaderboardInfo, entryName);
        if (!myLeaderboardInfo || !entryName) return '-';

        // If interval is provided, find the rank for that interval
        if (interval) {
            const entry = myLeaderboardInfo?.myInfo?.find(entry => entry.username === entryName);
            const intervalRank = entry?.intervalData?.find(i => i.interval === interval)?.rank;
            return intervalRank != null ? intervalRank : '-';
        } else {
            const rank = myLeaderboardInfo?.myInfo?.find(entry => entry.username === entryName)?.rank;
            return rank != null ? rank : '-';
        }
    }

    function findMyScore(myLeaderboardInfo, entryName, interval = null) {
        //console.log("Find My Score", myLeaderboardInfo, entryName, interval);
        if (!myLeaderboardInfo || !entryName) return null;
        //console.log("Past 1st check");
        const entry = myLeaderboardInfo?.myInfo?.find(entry => entry.username === entryName);
        if (!entry) return null;
        //console.log("Past 2nd check");
        // Helper to format golf score
        const formatGolfScore = (score) => {
            //console.log("Format Golf Score", score);
            if (score === 0) return 'Even';
            if (score > 0) return `+${score}`;
            return `${score}`; // Already negative
        };
    
        // If interval is provided, find the score for that interval
        if (interval) {
            const intervalScore = entry?.intervalData?.find(i => i.interval === interval);
            //console.log("Interval Score", intervalScore, intervalScore?.score, intervalScore?.intervalScore);
            return intervalScore?.intervalScore != null ? formatGolfScore(intervalScore?.intervalScore) : '-';
        } else {
            // Otherwise, return the overall score
            return entry?.overallScore != null ? formatGolfScore(entry.overallScore) : '-';
        }
    }
    

    // Memoize the number of picks to make for the currentInterval
    const picksToMakeTheCurrentInterval = useMemo(() => 
        theCurrentIntervalsTiersData?.tiers?.reduce((acc, tier) => acc + tier.picks, 0),
        [theCurrentIntervalsTiersData]
    );
    //console.log("Picks to make for the current interval", picksToMakeTheCurrentInterval, theCurrentIntervalsTiersData);

    function getGolfersAlive(picks) {
        //console.log("getGolfersAlive", picks);
        if (!picks || !picks.length) return 0;
        
        const golfersAlive = picks.filter(pick => 
            pick.interval === theCurrentInterval && 
            pick.cut !== true && 
            pick.withdrawn !== true &&
            pick.id && !pick.id.startsWith('no-pick')
        );

        return golfersAlive.length;
    }
    
    const totalEntriesCount = myLeaderboardInfo?.entryCount || 0;

    

    return (
        <Box>
            {entriesInThisPool?.length === 1 && !canIAddAnEntry && (
                <TiersSingleEntryView 
                    entry={entriesInThisPool[0]}
                    league={league}
                    theCurrentInterval={theCurrentInterval}
                    theCurrentIntervalsInfo={theCurrentIntervalsInfo}
                    setIndexToRemove={setIndexToRemove}
                    setRemoveModalOpen={setRemoveModalOpen}
                    isThisPoolPartyGame={isThisPoolPartyGame}
                    pickIndex={pickIndex}
                    isUserAdmin={isUserAdmin}
                    findMyRank={findMyRank}
                    findMyScore={findMyScore}
                    picksToMakeTheCurrentInterval={picksToMakeTheCurrentInterval}
                    getGolfersAlive={getGolfersAlive}
                    myLeaderboardInfo={myLeaderboardInfo}
                    totalEntriesCount={totalEntriesCount}
                    hasCurrentIntervalStarted={hasCurrentIntervalStarted}
                    originalPicks={originalPicks}
                    hasJoinDeadlinePassed={hasJoinDeadlinePassed}
                    oneIntervalEvent={oneIntervalEvent}
                    isUserOnOverallLeaderboard={isUserOnOverallLeaderboard}
                />
            )}
            {(entriesInThisPool?.length > 1 || canIAddAnEntry) && (
                <TiersMultipleEntryView 
                    entries={entriesInThisPool}
                    league={league}
                    theCurrentInterval={theCurrentInterval}
                    theCurrentIntervalsInfo={theCurrentIntervalsInfo}
                    setIndexToRemove={setIndexToRemove}
                    setRemoveModalOpen={setRemoveModalOpen}
                    isThisPoolPartyGame={isThisPoolPartyGame}
                    pickIndex={pickIndex}
                    isUserAdmin={isUserAdmin}
                    findMyRank={findMyRank}
                    findMyScore={findMyScore}
                    picksToMakeTheCurrentInterval={picksToMakeTheCurrentInterval}
                    getGolfersAlive={getGolfersAlive}
                    myLeaderboardInfo={myLeaderboardInfo}
                    totalEntriesCount={totalEntriesCount}
                    hasCurrentIntervalStarted={hasCurrentIntervalStarted}
                    originalPicks={originalPicks}
                    hasJoinDeadlinePassed={hasJoinDeadlinePassed}
                    setJoinOpen={setJoinOpen}
                    canIAddAnEntry={canIAddAnEntry}
                    oneIntervalEvent={oneIntervalEvent}
                    isUserOnOverallLeaderboard={isUserOnOverallLeaderboard}
                />
            )}
        </Box>
    );
}

export default TiersGameUserDash;